/**
 * The phone's view of More → Models.
 *
 * The gateway owns both halves of this — what each provider's CLI offers, and
 * which of those models chats and tasks may use — so this is a thin cache in
 * front of `/api/models` shared by every pane that needs it. One fetch serves
 * the config pane, the chat Settings picker, and the homescreen badges.
 *
 * Models the phone switched on that this build does not ship are handed to
 * `setDiscoveredModels` as they arrive, so labels, effort rungs, and the
 * backend a chat runs on resolve the same way here as on the gateway.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { api } from './api.ts';
import { setDiscoveredModels } from './models.ts';
import type { ModelCatalog, ProviderCatalog, ProviderModel } from './protocol.ts';

export interface CatalogState {
  providers: ProviderCatalog[];
  /** The allowlist, as rows. Empty means no filter: every model is offered. */
  enabled: ProviderModel[];
  loading: boolean;
  error: string | null;
  fetchedAt: number;
}

const EMPTY: CatalogState = {
  providers: [],
  enabled: [],
  loading: false,
  error: null,
  fetchedAt: 0,
};

let state: CatalogState = EMPTY;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(next: CatalogState): void {
  state = next;
  for (const listener of listeners) listener();
}

function adopt(catalog: ModelCatalog): void {
  const rows = catalog.providers.flatMap((p) => p.models);
  const enabled = catalog.enabled.flatMap((id) => {
    const row = rows.find((m) => m.id === id);
    return row ? [row] : [];
  });
  // Only the rows that are switched on: the registry is what the pickers and
  // the session rows read, and an unfiltered provider list would swamp them.
  setDiscoveredModels(enabled.filter((m) => !m.builtin));
  publish({
    providers: catalog.providers,
    enabled,
    loading: false,
    error: null,
    fetchedAt: catalog.fetchedAt,
  });
}

/** Ask the gateway. `refresh` re-runs each CLI instead of using its cache. */
export function loadCatalog(refresh = false): Promise<void> {
  if (inFlight && !refresh) return inFlight;
  publish({ ...state, loading: true, error: null });
  const done = api
    .modelCatalog(refresh)
    .then(adopt)
    .catch((err: unknown) => {
      publish({
        ...state,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    })
    .finally(() => {
      inFlight = null;
    });
  inFlight = done;
  return done;
}

export async function saveEnabledModels(rows: readonly ProviderModel[]): Promise<void> {
  const enabled = [...rows];
  // Optimistic: the toggles must not wait on a round trip to light up.
  setDiscoveredModels(enabled.filter((m) => !m.builtin));
  publish({ ...state, enabled });
  try {
    await api.setEnabledModels(enabled);
  } catch (err) {
    publish({ ...state, error: err instanceof Error ? err.message : String(err) });
    await loadCatalog();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The catalog, fetched once per page load unless a pane asks for a refresh. */
export function useModelCatalog(): CatalogState {
  const snapshot = useSyncExternalStore(subscribe, () => state);
  useEffect(() => {
    if (state.fetchedAt === 0 && !state.loading && state.error === null) void loadCatalog();
  }, []);
  return snapshot;
}

/** Ids chats and tasks may use. Empty means the picker offers everything. */
export function useEnabledModelIds(): string[] {
  return useModelCatalog().enabled.map((m) => m.id);
}
